class CompareProductSection {
  constructor(container) {
    this.container = container;
    this.mediaQuery = window.matchMedia('(min-width: 992px)');
    this.syncFrame = 0;
    this.onMediaChange = () => this.scheduleSync();
    this.onResize = () => this.scheduleSync();
    this.resizeObserver = new ResizeObserver(() => this.scheduleSync());

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.onMediaChange);
    } else {
      this.mediaQuery.addListener(this.onMediaChange);
    }

    window.addEventListener('resize', this.onResize);
    this.resizeObserver.observe(this.container);
    this.bindImageLoadEvents();
    this.syncHeights();
  }

  bindImageLoadEvents() {
    this.container.querySelectorAll('.compare-item-img-wrapper img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => this.scheduleSync(), { once: true });
    });
  }

  scheduleSync() {
    if (this.syncFrame) return;

    this.syncFrame = window.requestAnimationFrame(() => {
      this.syncFrame = 0;
      this.syncHeights();
    });
  }

  syncHeights() {
    if (!this.mediaQuery.matches) {
      this.resetHeights();
      return;
    }

    this.syncBlockHeights('.compare-item-img-wrapper');
    this.syncBlockHeights('.compare-item-wrapper', true);
  }

  syncBlockHeights(selector, syncChildren) {
    const blocks = this.container.querySelectorAll(selector);
    if (!blocks.length) return;

    if (syncChildren) {
      const rowCount = blocks[0].children.length;

      for (let i = 0; i < rowCount; i += 1) {
        const rows = [];

        blocks.forEach((block) => {
          const row = block.children[i];
          if (row) {
            row.style.minHeight = '';
            rows.push(row);
          }
        });

        const maxHeight = Math.max(...rows.map((row) => row.getBoundingClientRect().height), 0);
        rows.forEach((row) => {
          row.style.minHeight = `${maxHeight}px`;
        });
      }

      return;
    }

    blocks.forEach((block) => {
      block.style.minHeight = '';
    });

    const maxHeight = Math.max(...Array.from(blocks).map((block) => block.getBoundingClientRect().height), 0);
    blocks.forEach((block) => {
      block.style.minHeight = `${maxHeight}px`;
    });
  }

  resetHeights() {
    this.container.querySelectorAll('.compare-item-img-wrapper, .compare-item-wrapper > *').forEach((element) => {
      element.style.minHeight = '';
    });
  }

  destroy() {
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.onMediaChange);
    } else {
      this.mediaQuery.removeListener(this.onMediaChange);
    }

    window.removeEventListener('resize', this.onResize);
    if (this.syncFrame) {
      window.cancelAnimationFrame(this.syncFrame);
      this.syncFrame = 0;
    }
    this.resizeObserver.disconnect();
    this.resetHeights();
  }
}

const compareProductSections = new WeakMap();

function initCompareProduct(root) {
  const container = root.matches?.('.compare-product')
    ? root
    : root.querySelector?.('.compare-product');

  if (!container) return;

  destroyCompareProduct(root);

  compareProductSections.set(container, new CompareProductSection(container));
}

function destroyCompareProduct(root) {
  const container = root.matches?.('.compare-product')
    ? root
    : root.querySelector?.('.compare-product');

  if (!container) return;

  const instance = compareProductSections.get(container);
  if (instance) {
    instance.destroy();
    compareProductSections.delete(container);
  }
}

function initAllCompareProducts(scope = document) {
  scope.querySelectorAll('.compare-product').forEach((container) => {
    if (!compareProductSections.has(container)) {
      compareProductSections.set(container, new CompareProductSection(container));
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllCompareProducts());
} else {
  initAllCompareProducts();
}

document.addEventListener('shopify:section:load', (event) => {
  initCompareProduct(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyCompareProduct(event.target);
});
